import { useAtom } from 'jotai';
import type { JSX } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';

import type { PERMISSIONS_LOGIC } from '@/constants/common';
import useAuth, {
  authLoadingUserAtom,
  REDIRECT_STORAGE_KEY
} from '@/hooks/Auth.hooks';
import useCustomRequirementsCheck from '@/hooks/useCustomRequirementsCheck';
import usePermissionsCheck from '@/hooks/usePermissionsCheck';
import useProductsCheck from '@/hooks/useProductsCheck';
import NoAccess from '@/pages/NoAccess/NoAccess';

import type { CustomRequirementType } from '../CheckRequirements/CheckRequirements.types';
import { LoadingIndicator } from '../LoadingIndicator';

type PermissionsInput =
  | string
  | string[]
  | { permissions?: string[]; logic?: PERMISSIONS_LOGIC }
  | undefined;

function isPermissionConfig(
  value: PermissionsInput
): value is { permissions?: string[]; logic?: PERMISSIONS_LOGIC } {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// A wrapper component that checks if the user is authenticated and if the user has all the roles required to access the page
export const RequireAuth = ({
  children,
  defaultRoute,
  permissions,
  requiredProducts,
  customRequirements
}: {
  children: JSX.Element;
  defaultRoute?: boolean;
  permissions?:
    | string
    | string[]
    | { permissions: string[]; logic?: PERMISSIONS_LOGIC };
  requiredProducts?: { products: string[]; logic?: PERMISSIONS_LOGIC };
  customRequirements?: CustomRequirementType;
}) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [isLoading] = useAtom(authLoadingUserAtom); // can't use from useAuth because it will cause an dead loop
  const location = useLocation();

  const permissionList =
    typeof permissions === 'string'
      ? [permissions]
      : Array.isArray(permissions)
        ? permissions
        : isPermissionConfig(permissions)
          ? permissions.permissions || []
          : [];

  const permissionLogic = isPermissionConfig(permissions)
    ? permissions.logic
    : undefined;

  const meetsCustomRequirements =
    useCustomRequirementsCheck(customRequirements);
  const hasPermissions = usePermissionsCheck(permissionList, permissionLogic);

  const hasProducts = useProductsCheck(
    requiredProducts?.products,
    requiredProducts?.logic
  );

  if (isLoading) return <LoadingIndicator isLoading />;

  // For the default route if user does not have tickets.read permissions, will redirect to /announcements which all users should have access to
  if (defaultRoute && isAuthenticated && !hasPermissions) {
    navigate('/announcements');
    return null;
  }

  // If user is authenticated but doesn't have the required permissions, show the NoAccess page
  if (permissions && isAuthenticated && !hasPermissions) {
    return <NoAccess />;
  }

  // If the user is not authenticated or access token was removed from localstorage (another tab), redirect to the login page
  if (!isAuthenticated) {
    sessionStorage.setItem(
      REDIRECT_STORAGE_KEY,
      location.pathname + location.search
    );
    return <Navigate to="/login" replace />;
  }

  // If the user is authenticated but doesn't have the required products or requirements, show NoAccess page
  if (
    (requiredProducts && !hasProducts) ||
    (customRequirements && !meetsCustomRequirements)
  ) {
    return <NoAccess />;
  }

  return children;
};

export default RequireAuth;
